// =============================================================================
// AVAILABILITY SERVICE
// =============================================================================
// Service for fetching available time slots for client booking
// Uses availabilityBlocks from Firestore to determine available time slots

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  Timestamp 
} from 'firebase/firestore';
import { firestore as db } from './firestore';
import type { 
  TimeSlot, 
  AvailabilityResponse, 
  BookingRequest, 
  BookingResponse, 
  ScheduledCall 
} from '@/types/availability';

// Re-export types from availability types
export type { 
  TimeSlot, 
  AvailabilityResponse, 
  BookingRequest, 
  BookingResponse 
} from '@/types/availability';

// AvailabilityBlock type matching the admin console structure
interface AvailabilityBlock {
  id: string;
  title?: string;
  startTime: Date;
  endTime: Date;
  color?: string;
  recurrence?: 'none' | 'daily' | 'daily-workweek' | 'weekly' | 'monthly';
  recurrenceEnd?: Date;
  originalBlockId?: string;
  createdAt: Date;
  updatedAt: Date;
}

class AvailabilityService {
  private readonly COLLECTIONS = {
    SCHEDULED_CALLS: 'scheduledCalls',
    AVAILABILITY_BLOCKS: 'availabilityBlocks'
  };

  /**
   * Get available time slots for a specific date range
   */
  async getAvailableSlots(
    startDate: Date, 
    endDate: Date, 
    timezone: string = 'America/New_York'
  ): Promise<AvailabilityResponse> {
    try {
      // Get availability blocks from Firestore
      const availabilityBlocks = await this.getAvailabilityBlocks(startDate, endDate);
      
      // Get existing scheduled calls in the date range
      const scheduledCalls = await this.getScheduledCalls(startDate, endDate);

      // Generate time slots from availability blocks
      const timeSlots = this.generateTimeSlotsFromBlocks(
        startDate, 
        endDate, 
        availabilityBlocks,
        scheduledCalls,
        timezone
      );

      return {
        success: true,
        timeSlots
      };
    } catch (error) {
      console.error('Error getting available slots:', error);
      // If anything fails, return empty slots rather than error
      return {
        success: true,
        timeSlots: []
      };
    }
  }

  /**
   * Book a time slot - calls Cloud Function
   */
  async bookTimeSlot(bookingRequest: BookingRequest): Promise<BookingResponse> {
    try {
      // Call the cloud function to schedule the call
      const functionUrl = `${import.meta.env.VITE_FIREBASE_FUNCTIONS}/scheduleCall`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingRequest)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.error || 'Failed to schedule call'
        };
      }

      const result = await response.json();
      return {
        success: true,
        callId: result.callId
      };
    } catch (error) {
      console.error('Error booking time slot:', error);
      return {
        success: false,
        error: 'Failed to schedule call'
      };
    }
  }

  /**
   * Get availability blocks from Firestore that overlap with the date range
   */
  private async getAvailabilityBlocks(startDate: Date, endDate: Date): Promise<AvailabilityBlock[]> {
    try {
      if (!db) {
        console.warn('Firestore not initialized');
        return [];
      }

      const blocksRef = collection(db, this.COLLECTIONS.AVAILABILITY_BLOCKS);
      
      // Query for blocks that start before or during our range
      // We'll filter for overlap in memory since Firestore doesn't support complex range queries
      const q = query(
        blocksRef,
        where('startTime', '<=', Timestamp.fromDate(endDate)),
        orderBy('startTime', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const blocks: AvailabilityBlock[] = [];

      querySnapshot.forEach(doc => {
        const data = doc.data();
        const blockStart = data.startTime.toDate();
        const blockEnd = data.endTime.toDate();
        
        // Only include blocks that overlap with our date range
        // Block overlaps if: blockStart <= endDate && blockEnd >= startDate
        if (blockStart <= endDate && blockEnd >= startDate) {
          blocks.push({
            id: doc.id,
            title: data.title,
            startTime: blockStart,
            endTime: blockEnd,
            color: data.color,
            recurrence: data.recurrence || 'none',
            recurrenceEnd: data.recurrenceEnd?.toDate(),
            originalBlockId: data.originalBlockId,
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate()
          });
        }
      });

      return blocks;
    } catch (error) {
      console.error('Error fetching availability blocks:', error);
      return [];
    }
  }

  /**
   * Generate time slots from availability blocks
   */
  private generateTimeSlotsFromBlocks(
    startDate: Date,
    endDate: Date,
    availabilityBlocks: AvailabilityBlock[],
    scheduledCalls: ScheduledCall[],
    timezone: string
  ): TimeSlot[] {
    const timeSlots: TimeSlot[] = [];
    const now = new Date();

    // Process each availability block
    for (const block of availabilityBlocks) {
      const blockStart = new Date(block.startTime);
      const blockEnd = new Date(block.endTime);

      // Skip if block is entirely in the past
      if (blockEnd < now) {
        continue;
      }

      // Generate 30-minute slots within this block
      let currentSlotStart = new Date(Math.max(blockStart.getTime(), startDate.getTime()));
      
      // If block starts before our range, start at the beginning of our range
      if (blockStart < startDate) {
        currentSlotStart = new Date(startDate);
        // Round up to nearest 30-minute mark
        const minutes = currentSlotStart.getMinutes();
        if (minutes > 0 && minutes < 30) {
          currentSlotStart.setMinutes(30, 0, 0);
        } else if (minutes >= 30) {
          currentSlotStart.setHours(currentSlotStart.getHours() + 1, 0, 0, 0);
        }
      }

      const blockEndBoundary = new Date(Math.min(blockEnd.getTime(), endDate.getTime()));
      
      while (currentSlotStart < blockEndBoundary) {
        const slotEnd = new Date(currentSlotStart.getTime() + 30 * 60 * 1000);
        
        // Don't create slots that extend beyond the block
        if (slotEnd > blockEnd) {
          break;
        }

        // Skip if this is today and the slot is in the past
        if (currentSlotStart.toDateString() === now.toDateString() && currentSlotStart < now) {
          currentSlotStart = new Date(currentSlotStart.getTime() + 30 * 60 * 1000);
          continue;
        }

        // Check if this slot conflicts with existing bookings
        const isAvailable = !this.hasConflict(currentSlotStart, slotEnd, scheduledCalls);

        const dateStr = currentSlotStart.toISOString().split('T')[0];
        
        timeSlots.push({
          start: currentSlotStart.toISOString(),
          end: slotEnd.toISOString(),
          date: dateStr,
          timezone: timezone,
          available: isAvailable
        });

        // Move to next 30-minute slot
        currentSlotStart = new Date(currentSlotStart.getTime() + 30 * 60 * 1000);
      }
    }

    // Sort by start time
    timeSlots.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    return timeSlots;
  }

  /**
   * Get scheduled calls in date range
   */
  private async getScheduledCalls(startDate: Date, endDate: Date): Promise<ScheduledCall[]> {
    try {
      if (!db) {
        return [];
      }
      
      const callsRef = collection(db, this.COLLECTIONS.SCHEDULED_CALLS);
      const q = query(
        callsRef,
        where('startTime', '>=', Timestamp.fromDate(startDate)),
        where('startTime', '<=', Timestamp.fromDate(endDate)),
        where('status', 'in', ['scheduled', 'confirmed']),
        orderBy('startTime')
      );

      const querySnapshot = await getDocs(q);
      const calls: ScheduledCall[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        calls.push({
          id: doc.id,
          ...data,
          startTime: data.startTime,
          endTime: data.endTime
        } as ScheduledCall);
      });

      return calls;
    } catch (error) {
      console.error('Error fetching scheduled calls:', error);
      return [];
    }
  }

  /**
   * Check if a time slot conflicts with existing bookings
   */
  private hasConflict(
    start: Date,
    end: Date,
    scheduledCalls: ScheduledCall[]
  ): boolean {
    return scheduledCalls.some(call => {
      // Handle Firestore Timestamp or Date objects
      const callStart = (call.startTime as any)?.toDate ? (call.startTime as any).toDate() : new Date(call.startTime as any);
      const callEnd = (call.endTime as any)?.toDate ? (call.endTime as any).toDate() : new Date(call.endTime as any);

      // Check for overlap
      return (
        (start < callEnd && end > callStart) ||
        (callStart < end && callEnd > start)
      );
    });
  }
}

// Export singleton instance
export const availabilityService = new AvailabilityService();
